import { Stack, Typography } from "@mui/material";
import ConfirmationDialog from "components/UI/AcxConfirmationDialog";
import { useState } from "react";
import { useStore } from "utils/useStore";
import { ChainSourceStore } from "../../Stores/ChainSourceStore";
import { UnparentedPromptModelReference } from "../../Stores/ChainSourceHelpers";

interface PendingParents {
    references: UnparentedPromptModelReference[];
    /** Runs once the parents are added, so a held-up save can continue. */
    onAdded?: () => void;
}

/**
 * Manually typed model tokens are valid only when their producers are parents.
 * This confirmation keeps typing flexible without creating hidden dependencies.
 */
export function useAddPromptParentsConfirmation() {
    const store = useStore(ChainSourceStore);
    const [pending, setPending] = useState<PendingParents>({ references: [] });

    function dismiss() {
        setPending({ references: [] });
    }

    /** Checks a single prompt, as the step prompt loses focus. */
    function detectPromptParents(prompt: string) {
        setPending({ references: store.getUnparentedPromptReferences(prompt) });
    }

    /**
     * Opens the dialog and returns `true` when a prompt names an undeclared
     * parent, so the caller can hold off; `onAdded` runs once they are declared.
     * The save rejects these anyway, so asking here saves a failed round trip.
     */
    function confirmConfigurationParents(onAdded?: () => void): boolean {
        const references = store.getUnparentedConfigurationReferences();
        if (references.length === 0) return false;

        setPending({ references, onAdded });
        return true;
    }

    const confirmationDialog = (
        <ConfirmationDialog
            isOpen={pending.references.length > 0}
            title="Add referenced parent models?"
            subTitle="These prompt variables come from models that are not currently parents."
            confirmButtonText="Add parent models"
            cancelButtonText="Not now"
            onClose={dismiss}
            onCancel={dismiss}
            onConfirm={() => {
                pending.references.forEach(({ model }) =>
                    store.addParent(model),
                );
                dismiss();
                pending.onAdded?.();
            }}
            content={
                <Stack direction="column" rowGap={1.5}>
                    <Typography color="gray.600">
                        Adding them makes the dependency explicit and ensures
                        their outputs are available when this model runs.
                    </Typography>
                    {pending.references.map(({ model, variables }) => (
                        <Stack key={model.id} direction="column" rowGap={0.25}>
                            <Typography color="gray.700" fontWeight="bold">
                                {model.name}
                            </Typography>
                            <Typography color="gray.600">
                                {variables
                                    .map((variable) => `{{${variable}}}`)
                                    .join(", ")}
                            </Typography>
                        </Stack>
                    ))}
                    <Typography color="gray.600">
                        If you do not add them, the prompt stays as written but
                        the model cannot be saved until the references are
                        resolved.
                    </Typography>
                </Stack>
            }
        />
    );

    return {
        detectPromptParents,
        confirmConfigurationParents,
        confirmationDialog,
    };
}
